import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  CHANNEL_STATUS,
  CHANNEL_STATUS_OBJECT,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
} from '../../../../../enum/constants';
import { getIntegrationQrCode } from '../../../../../helpers/utils';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChannelService } from '../../../../../services/channel.service';
import { takeUntil } from 'rxjs';
import { BaseService } from '../../../../../services/base.service';
import { WebSocketService } from '../../../../../services/gateway.service';
import {
  IFacebookPageOption,
  ISocketIntegrationEvent,
  SocketIntegrationAction,
} from '../integration-instagram/integration.interface';
import { IntegrationService } from '../../../../../services/integration.service';
import { BaseIntegration } from '../base-integration/base-integration';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'app-integration-facebook',
  templateUrl: './integration-facebook.component.html',
  styleUrls: ['./integration-facebook.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationFacebookComponent extends BaseIntegration implements OnInit, OnDestroy {
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();
  protected facebookPages: IFacebookPageOption[] = [];
  protected selectedPageControl = new FormControl<string>('', {
    nonNullable: true,
    validators: Validators.required,
  });

  constructor(
    protected override readonly notificationService: NotificationService,
    protected override readonly channelService: ChannelService,
    protected override readonly integrationService: IntegrationService,
    protected readonly baseService: BaseService,
    protected readonly webSocketService: WebSocketService,
  ) {
    super(integrationService, notificationService, channelService);
  }

  @ViewChild('qrcode') qrCodeCanvas!: ElementRef;

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.channel_id = this.payload.channel_id;
    this.initSockets();
    this.getChannelById();
  }

  private initSockets() {
    this.webSocketService
      .facebookAuthAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleAuthSocketActions(data);
        },
      });
  }

  protected connectAccount() {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.FACEBOOK }).subscribe({
      next: (data: any) => {
        if (data) this.getFacebookLoginUrl();
      },
    });
  }

  private getFacebookLoginUrl(): void {
    const company = this.baseService.getCompanyObj();
    this.integrationService
      .getFacebookLoginUrl(this.channel_id, this.baseService.currentUserInfo.id, company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: { url: string }) => {
          this.facebookPages = [];
          this.selectedPageControl.reset('');
          this.qrUrl = data.url;
          this.showQr = true;
          requestAnimationFrame(() => this.initQRCode());
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: any) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
        },
      });
  }

  private initQRCode() {
    if (!this.qrCodeCanvas?.nativeElement) return;

    const qrCode = getIntegrationQrCode(this.qrUrl || '', '/assets/svgs/integrations/facebook.svg');

    this.qrCodeCanvas.nativeElement.innerHTML = '';
    qrCode.append(this.qrCodeCanvas.nativeElement);
  }

  private handleAuthSocketActions(event: ISocketIntegrationEvent) {
    if (this.channel_id !== event.channel_id) return;

    switch (event.action) {
      case SocketIntegrationAction.PROCESSED: {
        this.isLoader = true;
        this.showQr = false;
        this.facebookPages = [];
        this.selectedPageControl.reset('');
        break;
      }
      case SocketIntegrationAction.PAGES_AVAILABLE: {
        this.isLoader = false;
        this.showQr = false;
        this.facebookPages = event.data?.pages || [];
        this.selectOnlyAvailablePage();
        break;
      }
      case SocketIntegrationAction.SUCCESS: {
        this.getChannelById();
        this.showQr = false;
        this.facebookPages = [];
        this.selectedPageControl.reset('');
        break;
      }

      case SocketIntegrationAction.FAILED: {
        this.isLoader = false;
        this.showQr = false;
        if (event.error) {
          this.notificationService.showError('', ERROR_MESSAGES[event.error] || DEFAULT_ERROR);
        }
        break;
      }
    }
  }

  protected selectFacebookPage(): void {
    this.selectedPageControl.markAsTouched();
    if (this.selectedPageControl.invalid || !this.selectedPageControl.value) return;

    this.isLoader = true;
    this.integrationService
      .selectFacebookPage(this.channel_id, this.selectedPageControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.facebookPages = [];
          this.selectedPageControl.reset('');
          this.getChannelById();
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_updated,
          );
        },
        error: (e) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: any) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
        },
      });
  }

  protected selectPage(page: IFacebookPageOption): void {
    if (page.disabled) return;

    this.selectedPageControl.setValue(page.id);
    this.selectedPageControl.markAsTouched();
  }

  protected isSelectedPage(page: IFacebookPageOption): boolean {
    return this.selectedPageControl.value === page.id;
  }

  private selectOnlyAvailablePage(): void {
    const availablePages = this.facebookPages.filter((page) => !page.disabled);

    if (availablePages.length === 1) {
      this.selectedPageControl.setValue(availablePages[0].id);
    } else {
      this.selectedPageControl.reset('');
    }
  }

  protected readonly CHANNEL_STATUS = CHANNEL_STATUS;
  protected readonly CHANNEL_STATUS_OBJECT = CHANNEL_STATUS_OBJECT;
}
